export type ChatSurface = 'web' | 'teams' | 'm365_copilot';

/**
 * Shape of a disaster-chat session as the COMS listing / poll endpoints return
 * it (a `dgpt_sessions` row plus the joined owner attribution).
 * `pending_sources` is only present on rows the caller owns.
 */
export interface DisasterChatSummary {
  id: string;
  name: string;
  status: string;
  mode?: string;
  surface?: ChatSurface;
  timestamp?: string;
  archived?: boolean;
  user_guid: string;
  incident_id?: string | null;
  group_guid?: string | null;
  sitrep_id?: string | null;
  owner_name?: string | null;
  owner_email?: string | null;
  messages?: Array<{ role: string; content: string; timestamp?: string }>;
  pending_confirmation?: unknown;
  pending_sources?: unknown;
  /**
   * Input tokens of the chat's most recent model call — its live context size.
   * Null before the first call.
   */
  context_tokens?: number | null;
  /**
   * Times the chat's earlier turns have been summarized to fit the model's
   * context.
   */
  compaction_count?: number;
}

/**
 * Transcript entry COMS adds when it compacts a chat (PRAT-4843): the earlier
 * turns were summarized into the model's context. Rendered as a divider; never
 * sent to the model.
 */
export interface CompactionMarker {
  role: 'SYSTEM';
  kind: 'compaction';
  content: string;
  compaction_id: string;
  seq: number;
  reason: 'auto' | 'manual' | 'overflow';
  tokens_before: number;
  tokens_after: number;
  summary_text: string;
  timestamp: string;
}

export const isCompactionMarker = (
  message: unknown
): message is CompactionMarker =>
  !!message &&
  typeof message === 'object' &&
  (message as { role?: string }).role === 'SYSTEM' &&
  (message as { kind?: string }).kind === 'compaction';

/**
 * The selected chat in redux is either a full summary or `{}` for a brand-new
 * chat.
 */
export type SelectedDisasterChat = Partial<DisasterChatSummary>;

type ChatEntry = { role?: string };

/**
 * The USER message a reply at `index` answers: the nearest one before it,
 * skipping compaction dividers (a mid-turn compaction lands between them). -1
 * when there is none.
 */
export const findAnsweredUserIndex = (
  messages: readonly ChatEntry[],
  index: number
): number => {
  let i = index - 1;
  while (i >= 0 && isCompactionMarker(messages[i])) i--;
  return i >= 0 && messages[i]?.role === 'USER' ? i : -1;
};

/** Transcript entries worth exporting — compaction dividers are UI-only. */
export const exportableMessages = <T>(messages: readonly T[]): T[] =>
  messages.filter((m) => !isCompactionMarker(m));
